import { useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { graphql } from '@/gql';
import type { OrderAction, OrderStatus } from '@/gql/graphql';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { OverviewPanel } from '@/components/ui/OverviewPanel';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency, formatDateTime, formatShortId } from '@/lib/format';
import { paymentMethodLabel } from '@/lib/labels';
import { openStaffOrderReceipt } from '@/lib/staffReceiptDownload';
import { useBackHref } from '@/hooks/useListLocation';
import { WorkflowActionsCard } from '@/features/sales/WorkflowActionsCard';
import {
  ORDER_DETAIL_ACTIONS,
  orderActionLabel,
  orderActionVariant,
  sortOrderDetailActions,
} from './orderActions';

const OrderDoc = graphql(/* GraphQL */ `
  query Order($id: UUID!) {
    order(id: $id) {
      id
      status
      paymentStatus
      paymentMethod
      createdAt
      updatedAt
      recognitionDate
      grandTotal
      recognizedTotal
      originalGrandTotal
      amountDiscount
      amountPaid
      amountRefunded
      amountCanceled
      amountDue
      discount {
        id
        name
      }
      appliedVoucher {
        id
        label
        ruleName
      }
      device {
        id
        name
      }
      table {
        id
        name
      }
      tab {
        id
        name
      }
      category {
        id
        name
      }
      items {
        id
        productName
        quantity
        price
        tax
        lineTotal
        amountDiscount
      }
      payments {
        id
        status
        paymentMethod
        amount
        amountTip
        createdAt
      }
      refunds {
        id
        status
        paymentMethod
        amount
        createdAt
      }
      allowedActions
    }
  }
`);

const PerformOrderActionDoc = graphql(/* GraphQL */ `
  mutation PerformOrderAction($id: ID!, $action: OrderAction!) {
    performOrderAction(id: $id, action: $action) {
      id
      status
      paymentStatus
      allowedActions
    }
  }
`);

const STATUS_TONE: Record<OrderStatus, BadgeTone> = {
  NEW: 'neutral',
  OPEN: 'info',
  PENDING: 'info',
  CANCELING: 'warning',
  CANCELED: 'neutral',
  LOSS: 'error',
  FAILED: 'error',
  PARTIAL_PAID: 'warning',
  FULL_PAID: 'success',
  REFUNDING: 'warning',
  PARTIAL_REFUNDED: 'warning',
  FULL_REFUNDED: 'warning',
  REFUNDED: 'warning',
};

/**
 * Order detail: summary, workflow actions (when allowed), line items, payments, refunds.
 */
export function OrderDetailPage() {
  const { id = '' } = useParams();
  const apollo = useApolloClient();
  const backHref = useBackHref('/orders');
  const { data, previousData, loading, error } = useQuery(OrderDoc, { variables: { id } });
  const [performOrderAction, { loading: actionLoading }] = useMutation(PerformOrderActionDoc);
  const [actionError, setActionError] = useState<string | null>(null);
  const order = (data ?? previousData)?.order;
  const actions = order
    ? sortOrderDetailActions(
        order.allowedActions.filter((action) =>
          ORDER_DETAIL_ACTIONS.includes(action as OrderAction),
        ) as OrderAction[],
      )
    : [];

  if (loading && !order) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return (
      <EmptyState title="Could not load order" description={error.message} />
    );
  }
  if (!order) {
    return (
      <EmptyState
        title="Order not found"
        description="This order does not exist or has been deleted."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={backHref} id="admin.orders.back">
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to orders
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Order #{formatShortId(order.id)}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Created {formatDateTime(order.createdAt)} · Last updated {formatDateTime(order.updatedAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>
            {order.status.replace('_', ' ').toLowerCase()}
          </Badge>
          <Badge tone="neutral">payment: {order.paymentStatus}</Badge>
        </div>
      </div>

      <WorkflowActionsCard
        actions={actions}
        label={orderActionLabel}
        variant={orderActionVariant}
        actionLoading={actionLoading}
        error={actionError}
        onError={setActionError}
        onReceipt={() => openStaffOrderReceipt(apollo, order.id)}
        onAction={async (action) => {
          await performOrderAction({
            variables: { id: order.id, action },
            refetchQueries: [{ query: OrderDoc, variables: { id: order.id } }],
            awaitRefetchQueries: true,
          });
        }}
      />

      <OverviewPanel
        stats={[
          {
            id: 'total',
            label: 'Grand total',
            value: formatCurrency(order.grandTotal),
            hint:
              order.amountDiscount > 0
                ? `${formatCurrency(order.amountDiscount)} discount applied`
                : undefined,
          },
          {
            id: 'paid',
            label: 'Paid',
            value: formatCurrency(order.amountPaid),
          },
          {
            id: 'refunded',
            label: 'Refunded',
            value: formatCurrency(order.amountRefunded),
          },
          {
            id: 'due',
            label: 'Due',
            value: formatCurrency(order.amountDue),
          },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Summary" className="lg:col-span-1">
          <dl className="space-y-2 text-sm">
            <Row label="Category">{order.category?.name ?? '—'}</Row>
            <Row label="Device">{order.device?.name ?? '—'}</Row>
            <Row label="Table">{order.table?.name ?? '—'}</Row>
            <Row label="Tab">
              {order.tab ? (
                <Link to={`/tabs/${order.tab.id}`} className="text-brand hover:underline">
                  {order.tab.name || `#${formatShortId(order.tab.id)}`}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Recognition date">{order.recognitionDate ?? '—'}</Row>
            <Row label="Method">{paymentMethodLabel(order.paymentMethod)}</Row>
            <Row label="Discount">{order.discount?.name ?? '—'}</Row>
            <Row label="Voucher">
              {order.appliedVoucher ? (
                <>
                  <Link
                    to={`/vouchers?search=${encodeURIComponent(order.appliedVoucher.id)}`}
                    className="text-brand hover:underline"
                  >
                    #{formatShortId(order.appliedVoucher.id)}
                  </Link>
                  {order.appliedVoucher.label ? ` · ${order.appliedVoucher.label}` : null}
                  {order.appliedVoucher.ruleName ? ` · ${order.appliedVoucher.ruleName}` : null}
                </>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Recognized total">{formatCurrency(order.recognizedTotal)}</Row>
            <Row label="Original total">{formatCurrency(order.originalGrandTotal)}</Row>
            <Row label="Canceled">{formatCurrency(order.amountCanceled)}</Row>
          </dl>
        </Card>

        <Card title="Items" className="lg:col-span-2">
          {order.items.length === 0 ? (
            <EmptyState title="No items" description="This order has no line items." />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2">Product</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-right">Price</th>
                  <th className="px-2 py-2 text-right">Discount</th>
                  <th className="px-2 py-2 text-right">Line total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {order.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-2 py-2">{item.productName}</td>
                    <td className="px-2 py-2 text-right">{item.quantity}</td>
                    <td className="px-2 py-2 text-right">{formatCurrency(item.price)}</td>
                    <td className="px-2 py-2 text-right">
                      {item.amountDiscount > 0
                        ? formatCurrency(-item.amountDiscount)
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-right font-medium">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Payments">
          <TransactionTable
            rows={order.payments.map((p) => ({
              id: p.id,
              status: p.status,
              method: p.paymentMethod,
              amount: p.amount,
              tip: p.amountTip,
              createdAt: p.createdAt,
            }))}
            getIdHref={(paymentId) =>
              `/payments?search=${encodeURIComponent(paymentId)}`
            }
            showTip
          />
        </Card>
        <Card title="Refunds">
          <TransactionTable
            rows={order.refunds.map((r) => ({
              id: r.id,
              status: r.status,
              method: r.paymentMethod,
              amount: r.amount,
              createdAt: r.createdAt,
            }))}
            getIdHref={(refundId) =>
              `/refunds?search=${encodeURIComponent(refundId)}`
            }
          />
        </Card>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

interface TransactionRow {
  id: string;
  status: string;
  method: string;
  amount: number;
  tip?: number;
  createdAt: string;
}

function TransactionTable({
  rows,
  showTip,
  getIdHref,
}: {
  rows: TransactionRow[];
  showTip?: boolean;
  getIdHref: (id: string) => string;
}) {
  if (rows.length === 0) {
    return <EmptyState title="Nothing here" description="No records yet." />;
  }
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-xs uppercase tracking-wide text-gray-500">
        <tr>
          <th className="px-2 py-2">ID</th>
          <th className="px-2 py-2">Status</th>
          <th className="px-2 py-2">Method</th>
          <th className="px-2 py-2 text-right">Amount</th>
          {showTip && <th className="px-2 py-2 text-right">Tip</th>}
          <th className="px-2 py-2">Created</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-white/10">
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="px-2 py-2">
              <Link to={getIdHref(row.id)} className="text-brand hover:underline font-medium">
                #{formatShortId(row.id)}
              </Link>
            </td>
            <td className="px-2 py-2">
              <Badge tone={row.status === 'success' ? 'success' : row.status === 'failed' ? 'error' : 'neutral'}>
                {row.status}
              </Badge>
            </td>
            <td className="px-2 py-2">{row.method}</td>
            <td className="px-2 py-2 text-right font-medium">{formatCurrency(row.amount)}</td>
            {showTip && (
              <td className="px-2 py-2 text-right">
                {row.tip ? formatCurrency(row.tip) : '—'}
              </td>
            )}
            <td className="px-2 py-2">{formatDateTime(row.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
